import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import { FiChevronDown } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { CardAvatarStack } from '@/components/ui/card-avatar-stack';
import usePermission from '@/hooks/usePermission';
import { useSession } from '@/hooks/useSession';

// Mesmo padrão de linha das outras abas de Projetos (ProjectRowTeacher.jsx),
// mas com o shape de dado da aba Personalizados: cada `project_group` (id em
// `groupId`) tem um único `project` (participantes em vez de "X projetos").
//
// Reaproveitado também pelo detalhe de um grupo (GroupProjectDetailPlayfulTeacher.jsx),
// onde cada `project` é uma versão dentro do grupo em vez do grupo em si — daí
// `showEditOption` (não existe editar uma versão isolada, só o grupo).
//
// Vai direto pro workspace do projeto (sem passar pela tela "sobre", que o
// tema default ainda usa) — `backTo` é a rota da tela de origem (Grupo ou
// Personalizados), repassada via state pro botão de voltar em
// PlayfulTeacher/ProjectsContent.jsx. Se ainda não existe registro de acesso
// (`project-users`), cria silenciosamente antes de navegar — mesma chamada
// que a tela "sobre" faz ao clicar "Acessar projeto" (Default/ProjectsAbout.jsx).
export function PersonalizedProjectRowTeacher({ project, groupId, index = 0, onDelete, backTo, showEditOption = true }) {
  const navigate = useNavigate();
  const client = useClient();
  const { session } = useSession();

  const canEditPermission = usePermission('Editar projetos', 'Projetos');
  const canEdit = showEditOption && canEditPermission;
  const canDelete = usePermission('Excluir projetos', 'Projetos');
  const showActionsMenu = canEdit || canDelete;

  const isParticipant = project?.['project-users']?.some(projectUser => projectUser.user_id === session?.user?.id);
  const accessPath = `/projetos/${project?.id}`;

  const handleAccess = async () => {
    if (!isParticipant) {
      await client.mutate('project_users', { 'project-id': project?.id });
    }
    navigate(accessPath, { state: { backTo } });
  };

  return (
    <div
      className="flex flex-wrap items-center gap-4 rounded-[16px] border border-neutral-100 bg-white p-4 shadow-card transition-shadow duration-150 hover:shadow-card-hover"
      style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) ${250 + index * 70}ms both` }}
    >
      <div className="min-w-[200px] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3
            className="mb-0 line-clamp-2 text-base font-extrabold leading-snug"
            style={{ color: '#222731' }}
            title={project?.name}
          >
            {project?.name}
          </h3>

          <Badge
            variant={project?.approved ? 'success-soft' : 'info-soft'}
            className="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold normal-case"
          >
            {project?.approved ? 'Aprovado' : 'Em andamento'}
          </Badge>
        </div>

        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs font-semibold text-neutral-500">Participantes:</span>
          <CardAvatarStack
            data={project?.users}
            modalTitle={`Participantes do projeto ${project?.name}`}
          />
        </div>
      </div>

      <div className="flex w-full items-center gap-2 sm:w-auto sm:shrink-0">
        <PlayfulCardButton
          className="h-9 flex-1 px-6 sm:w-auto sm:flex-none"
          onClick={handleAccess}
        >
          Acessar
        </PlayfulCardButton>

        {showActionsMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <PlayfulCardButton
                variant="outline"
                aria-label="Opções"
                className="h-9 w-9 shrink-0 gap-0 p-0"
              >
                <FiChevronDown size={20} />
              </PlayfulCardButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              {canEdit && <DropdownMenuItem onSelect={() => navigate(`/projetos/criar/passo/curso-turma/${groupId}`)}>Editar</DropdownMenuItem>}
              {canDelete && <DropdownMenuItem onSelect={onDelete}>Excluir</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
